import Terminal from '@basementuniverse/react-animated-term';
import '@basementuniverse/react-animated-term/dist/react-animated-term.css';
import { faPause, faPlay } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from 'react-tooltip';
import { AnimatedTerminalAnimation } from '../types';
import classNames from '../utilities/class-names';
import { SnippetPiece } from '../utilities/snippet-render';
import './AnimatedTerminal.scss';
import { SNIPPET_CLASS_NAMES } from './Snippet';

const TERMINAL_ANIMATION_INTERVAL = 20;
const EDITOR_ANIMATION_INTERVAL = 12;
const TERMINAL_ANIMATION_DELAY = 4000;

/**
 * A row as the terminal renders it: its id is the index of its line
 */
type RenderedLine = {
  id: number;
  text: string;
  cmd: boolean;
};

/**
 * The first `length` characters of a highlighted row
 */
function highlightPrefix(pieces: SnippetPiece[], length: number): ReactNode[] {
  const nodes: ReactNode[] = [];
  let remaining = length;

  for (const [className, text] of pieces) {
    if (remaining <= 0) {
      break;
    }
    const visible = text.slice(0, remaining);
    remaining -= visible.length;
    nodes.push(
      className === null ? (
        visible
      ) : (
        <span key={nodes.length} className={SNIPPET_CLASS_NAMES[className]}>
          {visible}
        </span>
      )
    );
  }

  return nodes;
}

type AnimatedTerminalProps = {
  animations: AnimatedTerminalAnimation[];
};

export function AnimatedTerminal({
  animations,
}: AnimatedTerminalProps): ReactElement {
  const [play, setPlay] = useState(true);
  const [currentAnimationIndex, setCurrentAnimationIndex] = useState(0);
  const [animationTimeout, setAnimationTimeout] =
    useState<NodeJS.Timeout | null>(null);

  const handlePlayClick = () => {
    const newValue = !play;

    if (newValue) {
      const nextAnimationIndex =
        currentAnimationIndex >= animations.length - 1
          ? 0
          : currentAnimationIndex + 1;

      setCurrentAnimationIndex(nextAnimationIndex);
    }

    setPlay(newValue);
  };

  const handleSelectAnimationClick = (index: number) => {
    setCurrentAnimationIndex(index);
  };

  const handleAnimationCompleted = () => {
    const nextAnimationIndex =
      currentAnimationIndex >= animations.length - 1
        ? 0
        : currentAnimationIndex + 1;

    setAnimationTimeout(
      setTimeout(() => {
        setCurrentAnimationIndex(nextAnimationIndex);
      }, TERMINAL_ANIMATION_DELAY)
    );
  };

  useEffect(() => {
    if (!play && animationTimeout) {
      clearTimeout(animationTimeout);
    }
  }, [play, animationTimeout]);

  const tooltipId = `animated-terminal-tooltip-${useId()}`;

  const animation = animations[currentAnimationIndex];

  // The terminal restarts whenever its lines change (compared deeply, on every
  // frame), so it only gets the fields it uses
  const terminalLines = useMemo(
    () => animation?.lines.map(({ pieces, className, ...line }) => line) ?? [],
    [animation]
  );

  const renderLine = useCallback(
    (line: RenderedLine, row: number): ReactNode => {
      const source = animation?.lines[line.id];
      const pieces = source?.pieces?.[row];
      const rowText = source?.text.split('\n')[row] ?? '';

      // While a line shows spinner frames, its text isn't the highlighted text
      const content =
        pieces && rowText.startsWith(line.text)
          ? highlightPrefix(pieces, line.text.length)
          : line.text;

      return source?.className ? (
        <span className={source.className}>{content}</span>
      ) : (
        content
      );
    },
    [animation]
  );

  return (
    <>
      <div
        className={classNames(
          'animated-terminal',
          animation?.editor ? 'editor' : undefined
        )}
      >
        <div className="animations-container">
          {animations.length > 1 && (
            <>
              <button
                className={classNames(
                  'button-animation',
                  'button-animation-play'
                )}
                onClick={handlePlayClick}
              >
                <FontAwesomeIcon icon={play ? faPause : faPlay} />
              </button>
              {animations.map((animation, index) => (
                <button
                  className={classNames(
                    'button-animation',
                    'button-animation-select',
                    `button-animation-${index}`,
                    currentAnimationIndex === index ? 'selected' : undefined
                  )}
                  key={index}
                  data-tooltip-id={tooltipId}
                  data-tooltip-content={animation.title}
                  data-tooltip-place="bottom"
                  onClick={() => handleSelectAnimationClick(index)}
                ></button>
              ))}
            </>
          )}
        </div>
        {animation && (
          <Terminal
            lines={terminalLines}
            interval={
              animation.editor
                ? EDITOR_ANIMATION_INTERVAL
                : TERMINAL_ANIMATION_INTERVAL
            }
            lineNumbers={!!animation.editor}
            renderLine={renderLine}
            autoScroll
            onComplete={handleAnimationCompleted}
          />
        )}
      </div>
      {createPortal(
        <Tooltip id={tooltipId} className="animated-terminal-tooltip" />,
        document.body
      )}
    </>
  );
}
